import priorityService from '../../services/tickets/priority.service.js'

import entityUtility from '../../utils/entity.utility.js'
import httpUtility from '../../utils/http.utility.js'
import sortUtility from '../../utils/sort.utility.js'

import { useToast } from '../../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

async function savePriority(scope, payload) {
  try {
    let response
    if (payload._id) response = await priorityService.update.priority(scope, payload._id, payload)
    else response = await priorityService.create.priority(scope, payload)
    return response.data.item
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.savePriority')))
    return null
  }
}

async function deletePriority(scope, id) {
  try {
    await priorityService.delete.priority(scope, id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.deleteItem')))
    return false
  }
}

function sortPriorities(array) {
  return sortUtility.ascending.sort(array)
}

function mapPriorities(array) {
  return array.map((priority) => ({
    value: priority._id,
    label: priority.title,
    color: priority.color,
  }))
}

function findPriority(id, array) {
  return entityUtility.find(id, array)
}

class PriorityController {
  /**
   * Return a copy of `array` sorted ascending by `order`. Does not
   * mutate `array`.
   *
   * @template {{ order?: number|null, _id: string }} T
   * @param {T[]} array
   * @returns {T[]}
   */
  sort(array) {
    return sortPriorities(array)
  }

  /**
   * Map each priority to `{ value, label, color }`.
   *
   * @param {object[]} array
   * @returns {Array<{ value: string, label: string, color?: string }>}
   */
  map(array) {
    return mapPriorities(array)
  }

  /**
   * Look up one priority by `id` in `array`. Returns the row, or `null`
   * when `id` is missing or not found.
   *
   * @param {string|null|undefined} id
   * @param {Array<{ _id: string }>} array
   * @returns {object|null}
   */
  find(id, array) {
    return findPriority(id, array)
  }

  save = {
    /**
     * Create or update a priority row. Branches on `payload._id`
     * (truthy → update; omitted → create). Returns the saved priority on
     * success, `null` on failure (toasts the error).
     *
     * @param {string} scope - Settings API URL prefix (`ticket-account` or `ticket-group/:id`).
     * @param {object} payload
     * @returns {Promise<object|null>}
     */
    async priority(scope, payload) {
      return savePriority(scope, payload)
    },
  }

  delete = {
    /**
     * Delete a priority row. Returns `true` on success, `false` on
     * failure (toasts the error).
     *
     * @param {string} scope - Settings API URL prefix (`ticket-account` or `ticket-group/:id`).
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async priority(scope, id) {
      return deletePriority(scope, id)
    },
  }
}

export default new PriorityController()
